import { MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import t from "../translations";
import { valueOptionAriaLabel, type ValueOption } from "./axisValueLabels";
import { literalValue, type WizardParameter, type WizardValue } from "./clearanceRulesConfig";
import ValueOptionContent from "./ValueOptionContent";

type Props = {
  label: string;
  value: WizardValue;
  parameters: WizardParameter[];
  onChange: (value: WizardValue) => void;
  // When given, literal mode is a dropdown restricted to these options
  // (e.g. a severity bounded by an axis's max_value) - otherwise a free
  // number input (a condition's threshold, which isn't bounded).
  literalOptions?: ValueOption[];
  sx?: SxProps<Theme>;
};

export default function ValueField({
  label,
  value,
  parameters,
  onChange,
  literalOptions,
  sx,
}: Props) {
  // A parameter just added via ParametersSection starts with an empty name -
  // offering it here would let the admin select it and save an unreadable
  // "$" reference, so only ever expose ones that already have a real name.
  const validParameters = parameters.filter((param) => param.name !== "");

  const setMode = (mode: "literal" | "param" | null) => {
    if (mode === "literal") onChange(literalValue());
    else if (mode === "param") onChange({ kind: "param", name: validParameters[0]?.name ?? "" });
  };

  return (
    <Stack direction="row" spacing={1} sx={{ alignItems: "center", ...sx }}>
      {value.kind === "literal" ? (
        literalOptions ? (
          <TextField
            select
            size="small"
            label={label}
            value={value.value ?? ""}
            onChange={(event) => onChange(literalValue(Number(event.target.value)))}
            sx={{ minWidth: 160 }}
          >
            {literalOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} aria-label={valueOptionAriaLabel(option)}>
                <ValueOptionContent option={option} fullWidth />
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <TextField
            size="small"
            type="number"
            label={label}
            value={value.value ?? ""}
            onChange={(event) =>
              onChange(literalValue(event.target.value === "" ? null : Number(event.target.value)))
            }
            sx={{ width: 120 }}
          />
        )
      ) : (
        <TextField
          select
          size="small"
          label={label}
          value={value.name}
          onChange={(event) => onChange({ kind: "param", name: event.target.value })}
          sx={{ minWidth: 200 }}
        >
          {validParameters.map((param) => (
            <MenuItem key={param.key} value={param.name}>
              {`${param.name} = ${param.value}`}
            </MenuItem>
          ))}
        </TextField>
      )}
      {validParameters.length > 0 && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={value.kind}
          onChange={(_event, mode) => setMode(mode)}
        >
          <ToggleButton value="literal">{t.LiteralValueOption}</ToggleButton>
          <ToggleButton value="param">{t.ParameterValueOption}</ToggleButton>
        </ToggleButtonGroup>
      )}
    </Stack>
  );
}
